import React, { useEffect, useState } from 'react';
import { Slide, Grid } from "@material-ui/core";
import { Bar } from "react-chartjs-2";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    ChartEvent,
    ActiveElement,
    ChartOptions
} from 'chart.js';
import RequestAccessToken from "../../util/authService";
import BarChartIcon from '@material-ui/icons/BarChart';
import { refMDLoaded, practiceCoordinatorLoaded, shouldTryRefMDProfile, shouldTryCareCoordinatorProfile } from '../common/ProfileHelper'


interface DischargeDataPoint {
    x: number;
    y: string;
    meta: {
        dischargeType: string;
    };
}

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;

type OwnProps = {
    openDrillDownModal: (dischargeType: string) => void;
};

type NextSiteOfCareStatsProps = OwnProps & StateProps & DispatchProps;

const DischargeDispositionType = (props: NextSiteOfCareStatsProps) => {
    let {
        DischargeDispositionStats,
        requestDischargeDispositionStatsForProvider,
        requestCurrentUserCareCoordinatorProfile,
        userRefMdProfile,
        requestVitalsMdProfile,
        userPracticeCoordinatorProfile,
        receiveTokenResponse,
        APIToken,
        openDrillDownModal } = props;

    let [tokenState, setTokenState] = useState<APIStore.TokenType>();

    ChartJS.register(
        CategoryScale,
        LinearScale,
        BarElement,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend,
        ArcElement
    );

    const options: ChartOptions<'bar'> = {
        indexAxis: 'y' as const,
        responsive: true,
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: 'Next Sites of Care'
            },
        },
        onClick: (_event: ChartEvent, elements: ActiveElement[], chart: ChartJS) => {
            if (!elements.length) return;

            const { datasetIndex, index } = elements[0];
            const point = chart.data.datasets[datasetIndex].data[index] as unknown as DischargeDataPoint;

            openDrillDownModal(point.meta.dischargeType);
        },
        onHover: (event: ChartEvent, elements: ActiveElement[]) => {
            const target = event?.native?.target as HTMLElement;
            if (target) {
                target.style.cursor = elements.length ? "pointer" : "default";
            }
        }
    };

    let DischargeDispositions: APIStore.DischargeDispositionStatistics[] = [];


    let tokenStatePr = RequestAccessToken()
        .then((token) => {
            if (token && !tokenState) {
                setTokenState(token);
                receiveTokenResponse(token);
            }
        });

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }

    }, [APIToken, tokenState])

    useEffect(() => {
        if (shouldTryRefMDProfile(userRefMdProfile) && tokenState?.accessToken && userPracticeCoordinatorProfile.notFound) {
            requestVitalsMdProfile(tokenState.accessToken);
        }

    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile])

    useEffect(() => {
        if (shouldTryCareCoordinatorProfile(userPracticeCoordinatorProfile) && tokenState?.accessToken) {
            requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);
        }
    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile]);

    useEffect(() => {
        if ((refMDLoaded(userRefMdProfile) && tokenState?.accessToken)) {
            requestDischargeDispositionStatsForProvider(tokenState.accessToken)
        }

    }, [tokenState, userRefMdProfile]);

    useEffect(() => {
        if (!DischargeDispositionStats.isLoaded && !DischargeDispositionStats.isLoading && tokenState?.accessToken) {
            if (practiceCoordinatorLoaded(userPracticeCoordinatorProfile)) {
                requestDischargeDispositionStatsForProvider(tokenState.accessToken)
            }
        }
    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile, DischargeDispositionStats]);


    if (DischargeDispositionStats.isErrored) {
        return (<React.Fragment><p>Error Loading</p></React.Fragment>)
    }

    if (DischargeDispositionStats.isLoading || !DischargeDispositionStats.isLoaded) {
        return (
            <Slide in timeout={1000} direction="left">
                <Grid container>
                    <Grid item md={4}>
                        <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled"></BarChartIcon>
                    </Grid>
                </Grid>
            </Slide>

        )
    }

    if (DischargeDispositionStats) {
        DischargeDispositions = DischargeDispositionStats.dischargeDispositions;
    }

    let values: DischargeDataPoint[] = DischargeDispositions.map((val: APIStore.DischargeDispositionStatistics) => {
        return {
            x: val.patientCount,
            y: val.typeDesciption,
            meta: {
                dischargeType: val.typeDesciption
            }
        };
    });

    const data = {
        datasets: [
            {
                label: "Count",
                data: values,
                backgroundColor: [
                    'rgba(82, 189, 228, 0.2)',
                    'rgba(48, 82, 98, 0.2)',
                    'rgba(165, 209, 229, 0.2)',
                    'rgba(84, 144, 174, 0.2)',
                    'rgba(71, 91, 100, 0.2)',
                    'rgba(141, 206, 233, 0.2)',
                    'rgba(125, 151, 158, 0.2)'
                ],
                borderColor: [
                    'rgb(82, 189, 228)',
                    'rgb(48, 82, 98)',
                    'rgb(165, 209, 229)',
                    'rgb(84, 144, 174)',
                    'rgb(71, 91, 100)',
                    'rgb(141, 206, 233)',
                    'rgb(125, 151, 158)'
                ],
                hoverOffset: 4,
                borderWidth: 2
            },
        ],
    };

    return (
        <React.Fragment>
            <div>
                <React.Fragment>
                    <Bar options={options} data={data}></Bar>
                </React.Fragment>
            </div>
        </React.Fragment>
    )
}


export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    mapState,
    APIStore.actionCreators
)(DischargeDispositionType as any);